'use client';

import { Command } from 'cmdk';
import { Check, ChevronsUpDown, Loader2, Plus, X } from 'lucide-react';
import React, { useEffect, useId, useRef, useState } from 'react';

import { Label } from '../../primitives/label';
import { Popover, PopoverContent, PopoverTrigger } from '../../primitives/popover';
import { cn } from '../../utils/cn';

export interface UzComboboxOption<TValue extends string = string> {
  value: TValue;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface UzComboboxProps<TValue extends string = string> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  value?: TValue;
  onValueChange?: (value: TValue) => void;
  options: ReadonlyArray<UzComboboxOption<TValue>>;
  /**
   * When provided, the parent owns filtering (server-side search): local
   * fuzzy-filtering is turned off and this fires (debounced) as the user types,
   * so `options` should be the server's results for the current query. Omit for
   * a plain client-side filtered combobox.
   */
  onSearchChange?: (query: string) => void;
  /** Debounce (ms) before `onSearchChange` fires. Default 250. */
  searchDebounceMs?: number;
  loading?: boolean;
  disabled?: boolean;
  emptyText?: string;
  name?: string;
  containerClassName?: string;
  triggerClassName?: string;
  /**
   * Makes the combobox creatable: when the typed query has no exact label match,
   * a "create" row appears at the bottom that calls this with the trimmed query.
   */
  onCreate?: (label: string) => void;
  /** Builds the create-row text. Default: `Add "<query>"`. */
  createLabel?: (input: string) => string;
  /**
   * Names that already exist (e.g. the full master list, including items not in
   * `options` because they're already selected). The create row is suppressed
   * when the query matches one of these - so duplicates can't be created.
   */
  existingLabels?: ReadonlyArray<string>;
  /**
   * When true and a value is selected, shows a clear (X) control on the
   * trigger so the operator can unset the selection without hunting for an
   * empty option.
   */
  clearable?: boolean;
  /** Called when the clear control is clicked. Prefer this over sending an
   *  empty string through `onValueChange` for nullable fields. */
  onClear?: () => void;
  /**
   * Shown in the trigger as the current value (not greyed like the
   * placeholder) while nothing in `options` is selected - e.g. a name saved
   * as free text before a master pick existed.
   */
  fallbackLabel?: string;
}

/**
 * Searchable, scrollable single-select. Built on cmdk + Popover so large option
 * sets (e.g. thousands of TBO cities) stay usable: a search box up top, a capped
 * scroll area below. Pass `onSearchChange` to drive results from the server
 * (the component then shows whatever `options` you hand back); omit it for
 * local filtering. Mirrors UzSelect's label/hint/error shell.
 */
export const UzCombobox = <TValue extends string = string>({
  label,
  hint,
  error,
  required,
  placeholder = 'Select an option',
  fallbackLabel,
  searchPlaceholder = 'Search…',
  value,
  onValueChange,
  options,
  onSearchChange,
  searchDebounceMs = 250,
  loading,
  disabled,
  emptyText = 'No results found',
  name,
  containerClassName,
  triggerClassName,
  onCreate,
  createLabel,
  existingLabels,
  clearable,
  onClear,
}: UzComboboxProps<TValue>): React.JSX.Element => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const serverMode = typeof onSearchChange === 'function';

  // Debounce server-side search so we don't round-trip on every keystroke.
  const onSearchRef = useRef(onSearchChange);
  onSearchRef.current = onSearchChange;
  useEffect(() => {
    if (!serverMode) return;
    const t = setTimeout(() => onSearchRef.current?.(query.trim()), searchDebounceMs);
    return () => clearTimeout(t);
  }, [query, serverMode, searchDebounceMs]);

  const selected = options.find((o) => o.value === value);
  const showClear = Boolean(clearable && value && !disabled);

  const handleSelect = (next: TValue) => {
    onValueChange?.(next);
    setOpen(false);
  };

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <Label htmlFor={id} className={cn(disabled && 'opacity-60')}>
          {label}
          {required && (
            <span className="text-destructive ml-1 align-middle text-base leading-none font-semibold">
              *
            </span>
          )}
        </Label>
      )}
      {/* `modal`: inside a UzDialog the dialog's scroll lock swallows wheel
          events on the portaled list; a modal popover takes over the lock so
          the options scroll (shadcn "combobox in dialog" fix). */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            role="combobox"
            name={name}
            aria-expanded={open}
            aria-invalid={!!error || undefined}
            disabled={disabled}
            className={cn(
              'border-input bg-background flex h-10 w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors',
              // Focus highlights the field's own border (no offset ring sitting outside it).
              'focus:border-ring focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive focus:border-destructive',
              triggerClassName,
            )}
          >
            <span
              className={cn('truncate', !selected && !fallbackLabel && 'text-muted-foreground')}
            >
              {selected ? selected.label : (fallbackLabel ?? placeholder)}
            </span>
            <span className="ml-2 flex shrink-0 items-center gap-1">
              {showClear && (
                <span
                  aria-label="Clear selection"
                  // PointerDown + stopPropagation so we clear without nesting a
                  // <button> inside the trigger and without opening the popover.
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClear?.();
                    setOpen(false);
                  }}
                  className="text-muted-foreground hover:text-foreground rounded-sm p-0.5"
                >
                  <X className="h-3.5 w-3.5" />
                </span>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--radix-popover-trigger-width) p-0">
          <Command shouldFilter={!serverMode} loop>
            <div className="flex items-center border-b px-3">
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder={searchPlaceholder}
                className="placeholder:text-muted-foreground h-10 w-full bg-transparent text-sm outline-none"
              />
              {loading && <Loader2 className="text-muted-foreground ml-2 h-4 w-4 animate-spin" />}
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1">
              {!loading && (
                <Command.Empty className="text-muted-foreground py-6 text-center text-sm">
                  {emptyText}
                </Command.Empty>
              )}
              {options.map((o) => (
                <Command.Item
                  key={o.value}
                  value={serverMode ? o.value : `${o.label} ${o.value}`}
                  disabled={o.disabled}
                  onSelect={() => handleSelect(o.value)}
                  className={cn(
                    'flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm',
                    'aria-selected:bg-accent aria-selected:text-accent-foreground data-[disabled=true]:opacity-50',
                  )}
                >
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate">{o.label}</span>
                    {o.description && (
                      <span className="text-muted-foreground truncate text-xs">
                        {o.description}
                      </span>
                    )}
                  </span>
                  {o.value === value && <Check className="h-4 w-4 shrink-0" />}
                </Command.Item>
              ))}
              {onCreate &&
                query.trim() !== '' &&
                !options.some((o) => o.label.toLowerCase() === query.trim().toLowerCase()) &&
                !(existingLabels ?? []).some(
                  (l) => l.toLowerCase() === query.trim().toLowerCase(),
                ) && (
                  <Command.Item
                    key="__uz_create__"
                    value={query}
                    onSelect={() => {
                      onCreate(query.trim());
                      setQuery('');
                      setOpen(false);
                    }}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                      'aria-selected:bg-accent aria-selected:text-accent-foreground',
                    )}
                  >
                    <Plus className="h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {createLabel ? createLabel(query.trim()) : `Add “${query.trim()}”`}
                    </span>
                  </Command.Item>
                )}
            </Command.List>
          </Command>
        </PopoverContent>
      </Popover>
      {hint && !error && <p className="text-muted-foreground text-xs">{hint}</p>}
      {error && <p className="text-destructive text-xs">{error}</p>}
    </div>
  );
};
